/**
 * D212: the parked question a corrected VERB earns — "Did you mean TAKE THE
 * DRUM?" — and its two answers.
 *
 * @remarks
 * The parser reports a `verb_corrected` miss with the clause as the fuzzy
 * rung read it; the teach for that miss is `correctionConfirm`, already
 * spoken by the parse-failure arm. This module parks the question those
 * words asked, as a D179 `confirm`: the chips at the input line are its
 * buttons, "yes" runs the corrected clause as if the player had typed it
 * (echoing what they DID type — the transcript keeps the player's words),
 * "no" says so and closes, and any other input closes the question silently
 * and is read as itself, because a gate is not a conversation.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { foldCharacters } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PlayerInputOutcome } from './process-player-input.js';

import { bumpResponseUsage, speakSlot, type SpokenResponse } from '../defaults/default-responses.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { type PendingConfirmQuestion } from './chrome-directives.js';
import { parkQuestion } from './pending-question.js';

/** The words either side of a confirm accepts beside its chip's own label. */
export const PROCEED_WORDS: readonly string[] = ['yes', 'y'];
export const DECLINE_WORDS: readonly string[] = ['no', 'n'];

/** A slot's one line, resolved as a chip label — read, never stamped. */
const labelOf = (bundle: EngineGameBundle, state: RuntimeState, spoken: SpokenResponse): string =>
	resolveNarrativeLines([spoken.line], bundle, state)
		.map((line) => line.text)
		.join('');

/**
 * Park the correction question on a `verb_corrected` miss; leave every other
 * outcome untouched.
 *
 * @param bundle - The loaded bundle
 * @param outcome - The input's outcome, the teach already spoken
 * @returns The outcome with the confirm parked, or the outcome as it was
 */
export const withCorrectionQuestion = (bundle: EngineGameBundle, outcome: PlayerInputOutcome): PlayerInputOutcome => {
	if (outcome.type !== 'parse_failed' || outcome.diagnostics.failureKind !== 'verb_corrected') {
		return outcome;
	}

	const { correctedClauseText } = outcome.diagnostics;

	// The parser attaches the corrected clause to every verb_corrected report;
	// the guard is for the type, which cannot know — a breach parks nothing.
	if (correctedClauseText === undefined) {
		return outcome;
	}

	const state = outcome.nextState;
	const proceed = speakSlot(bundle, state, 'correctionProceedLabel');
	const keep = speakSlot(bundle, state, 'correctionKeepLabel');
	const proceedLabel = labelOf(bundle, state, proceed);
	const keepLabel = labelOf(bundle, state, keep);
	const question: PendingConfirmQuestion = {
		act: 'run_correction',
		askedAtInputOrdinal: state.session.commandHistory.length,
		clauseText: outcome.diagnostics.failedClauseText ?? correctedClauseText,
		correctedClauseText,
		// The chip's own label first, then the words either side accepts — once each, since the built-in label IS one of them.
		decline: { label: keepLabel, words: [...new Set([foldCharacters(keepLabel), ...DECLINE_WORDS])]},
		kind: 'confirm',
		proceed: { label: proceedLabel, words: [...new Set([foldCharacters(proceedLabel), ...PROCEED_WORDS])]},
		// The SPOKEN text, off the transcript, so the surface shows exactly what was said (the D80 rule).
		questionText: outcome.transcriptEntries.findLast((entry) => entry.triggerContext === 'parse_teach')?.text ?? '',
		roomId: state.progression.activeRoomId,
	};

	return {
		...outcome,
		nextState: parkQuestion(
			{ ...state, session: { ...state.session, responseUsage: bumpResponseUsage(state.session.responseUsage, [proceed.slot, keep.slot]) }},
			question,
		),
	};
};
